import React from "react";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";

const H2 = ({ children }) => <h2 className="font-heading text-2xl mt-10 mb-3 font-semibold text-slate-900">{children}</h2>;
const P = ({ children }) => <p className="text-slate-600 leading-relaxed mb-3">{children}</p>;
const LI = ({ children }) => <li className="text-slate-600 leading-relaxed mb-1.5 ml-5 list-disc">{children}</li>;

export default function TermsPage() {
    return (
        <PublicLayout>
            <Seo
                title="Allgemeine Geschäftsbedingungen (AGB) | Bewerbung24.ai"
                description="Die Allgemeinen Geschäftsbedingungen der KI-Bewerbungsplattform Bewerbung24.ai: Leistungen, Preise, Widerruf, Kündigung und Haftung."
                path="/agb"
            />
            <section className="max-w-3xl mx-auto px-6 lg:px-8 py-20" data-testid="terms-page">
                <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">Allgemeine Geschäftsbedingungen (AGB)</h1>
                <p className="text-slate-500">Stand: Juni 2026</p>

                <H2>§ 1 Geltungsbereich und Anbieter</H2>
                <P>
                    Diese AGB gelten für alle Verträge zwischen Bewerbung24.ai, Inhaberin Frau E. Decker, Am Kanal 22, 30599 Hannover
                    (Zweigstelle: Im Mediapark 8, 50670 Köln) — nachfolgend „Anbieter" — und den Nutzerinnen und Nutzern
                    (nachfolgend „Nutzer") der Plattform www.bewerbung24.ai.
                    Kontakt: info@bewerbung24.ai · Soforthilfe: +49 170 5721522.
                </P>

                <H2>§ 2 Leistungsbeschreibung</H2>
                <P>Der Anbieter stellt eine KI-gestützte Online-Plattform zur Verfügung, mit der Nutzer Bewerbungsunterlagen erstellen und verwalten können. Der Leistungsumfang umfasst je nach gebuchtem Tarif insbesondere:</P>
                <ul className="mb-3">
                    <LI>KI-generierte Anschreiben und Lebensläufe auf Basis der vom Nutzer bereitgestellten Daten und Stellenanzeigen</LI>
                    <LI>Dokumentenerkennung (OCR) für hochgeladene Zeugnisse, Lebensläufe und Stellenanzeigen</LI>
                    <LI>Design-Vorlagen und PDF-/DOCX-Export der Bewerbungsmappe</LI>
                    <LI>KI-Bewerbungsfoto-Optimierung und -Erstellung</LI>
                    <LI>Stellenanzeigen-Abgleich, ATS-Score und automatische Übersetzung</LI>
                    <LI>Optional: Autopilot-KI (automatisierte Stellensuche und Bewerbungsversand im Namen des Nutzers)</LI>
                    <LI>Optional: Interview-Coach (KI-gestützte Gesprächssimulation mit Feedback)</LI>
                </ul>

                <H2>§ 3 Registrierung und Testversion</H2>
                <P>
                    Die Nutzung setzt eine kostenlose Registrierung voraus. Der Nutzer versichert, wahrheitsgemäße Angaben zu machen und seine Zugangsdaten geheim zu halten.
                    Nach der Registrierung erhält jeder Nutzer automatisch eine kostenlose Testversion von 3 Tagen: eine Bewerbung inklusive, Export als PDF (Word-Export erst nach Kauf).
                    Nach Ablauf der Testphase ist für die weitere Nutzung der Abschluss eines Abonnements oder der Kauf von Bewerbungs-Guthaben erforderlich; bereits erstellte Inhalte bleiben erhalten.
                </P>

                <H2>§ 4 Tarife, Preise und Zahlung</H2>
                <P>Es gelten die zum Zeitpunkt der Bestellung auf der Preisseite angegebenen Preise inklusive gesetzlicher Mehrwertsteuer. Angeboten werden:</P>
                <ul className="mb-3">
                    <LI><strong>Einzelkauf / Pakete (Guthaben):</strong> Einmalzahlung für 1, 10, 20 oder eine frei wählbare Anzahl an Bewerbungen. Das Guthaben verfällt nicht und ist frei auf Stellen verteilbar.</LI>
                    <LI><strong>Abonnements (Starter, Pro, Unlimited):</strong> monatliche Zahlung, automatische Verlängerung um jeweils einen Monat, jederzeit zum Ende des Abrechnungszeitraums kündbar. Keine Mindestlaufzeit.</LI>
                    <LI><strong>Zusatzfunktionen (Add-ons):</strong> Autopilot-KI als monatliches Add-on-Abo; Interview-Coach als einmalige Freischaltung. Beide sind im Unlimited-Abo bereits enthalten.</LI>
                </ul>
                <P>
                    Die Zahlungsabwicklung erfolgt über den Zahlungsdienstleister Stripe. Der Vertrag kommt mit erfolgreichem Abschluss des Bezahlvorgangs zustande; die Freischaltung erfolgt unmittelbar danach.
                    Bei zeitlich befristeten Aktionspreisen gilt der Aktionspreis nur für Käufe innerhalb des angegebenen Aktionszeitraums.
                </P>

                <H2>§ 5 Laufzeit und Kündigung</H2>
                <P>
                    Abonnements können jederzeit mit Wirkung zum Ende des laufenden Abrechnungsmonats im Konto gekündigt werden — ohne Frist und ohne Angabe von Gründen.
                    Bis zum Laufzeitende bleibt der volle Funktionsumfang erhalten. Bereits gezahlte Beträge für den laufenden Monat werden nicht erstattet.
                    Einmalkäufe (Guthaben, Interview-Coach) sind nicht kündigungsbedürftig.
                </P>

                <H2>§ 6 Widerrufsrecht</H2>
                <P>
                    Verbrauchern steht grundsätzlich ein 14-tägiges Widerrufsrecht zu. Bei digitalen Inhalten erlischt das Widerrufsrecht gemäß § 356 Abs. 5 BGB,
                    wenn der Nutzer ausdrücklich zustimmt, dass mit der Ausführung vor Ablauf der Widerrufsfrist begonnen wird, und seine Kenntnis vom Erlöschen des Widerrufsrechts bestätigt.
                    Diese Zustimmung erfolgt im Bestellprozess. Für noch nicht genutzte Leistungen kannst du dich jederzeit an info@bewerbung24.ai wenden — wir finden eine faire Lösung.
                </P>

                <H2>§ 7 Pflichten des Nutzers</H2>
                <ul className="mb-3">
                    <LI>Der Nutzer stellt sicher, dass alle eingegebenen und hochgeladenen Inhalte wahrheitsgemäß sind und keine Rechte Dritter verletzen.</LI>
                    <LI>KI-generierte Bewerbungsunterlagen sind vor dem Versand vom Nutzer inhaltlich zu prüfen. Der Nutzer trägt die Verantwortung für die versendeten Inhalte.</LI>
                    <LI>Bei Nutzung der Autopilot-Funktion beauftragt der Nutzer den Anbieter ausdrücklich, Bewerbungen in seinem Namen per E-Mail zu versenden. Der Nutzer kann die Funktion jederzeit deaktivieren.</LI>
                    <LI>Eine missbräuchliche Nutzung (z. B. Massenversand ohne Bewerbungsabsicht, Täuschung, rechtswidrige Inhalte) ist untersagt und berechtigt den Anbieter zur Sperrung des Kontos.</LI>
                </ul>

                <H2>§ 8 KI-Hinweis und Gewährleistung</H2>
                <P>
                    Die Inhalte werden mithilfe künstlicher Intelligenz erzeugt. Der Anbieter übernimmt keine Gewähr für die inhaltliche Richtigkeit,
                    Vollständigkeit oder Eignung der generierten Texte im Einzelfall und insbesondere keine Garantie für den Erfolg einer Bewerbung
                    (z. B. Einladung zum Vorstellungsgespräch oder Anstellung). ATS-Scores und Job-Matching-Ergebnisse sind unverbindliche Einschätzungen.
                </P>

                <H2>§ 9 Verfügbarkeit</H2>
                <P>
                    Der Anbieter bemüht sich um eine hohe Verfügbarkeit der Plattform, schuldet jedoch keine ununterbrochene Erreichbarkeit.
                    Wartungsarbeiten und Störungen bei Drittanbietern (z. B. KI-Diensten, Zahlungsdienstleister) können die Nutzung vorübergehend einschränken.
                </P>

                <H2>§ 10 Haftung</H2>
                <P>
                    Der Anbieter haftet unbeschränkt bei Vorsatz, grober Fahrlässigkeit sowie bei Verletzung von Leben, Körper oder Gesundheit.
                    Bei einfacher Fahrlässigkeit haftet der Anbieter nur für die Verletzung wesentlicher Vertragspflichten (Kardinalpflichten),
                    begrenzt auf den vertragstypischen, vorhersehbaren Schaden. Die Haftung nach dem Produkthaftungsgesetz bleibt unberührt.
                </P>

                <H2>§ 11 Datenschutz</H2>
                <P>
                    Informationen zur Verarbeitung personenbezogener Daten findest du in unserer{" "}
                    <a href="/datenschutz" className="text-red-600 hover:underline">Datenschutzerklärung</a>.
                </P>

                <H2>§ 12 Schlussbestimmungen</H2>
                <P>
                    Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts. Gegenüber Verbrauchern gilt diese Rechtswahl nur,
                    soweit hierdurch keine zwingenden Verbraucherschutzvorschriften des Staates entzogen werden, in dem der Verbraucher seinen gewöhnlichen Aufenthalt hat.
                    Sollten einzelne Bestimmungen dieser AGB unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.
                </P>
            </section>
        </PublicLayout>
    );
}
